/** Sesion movil: perfil + permisos + tenant activo con su nombre. Sin expulsion por inactividad. */
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { authService, type Membership, type MobileUser } from '@/services/authService';
import { clearReadCaches } from '@/utils/pwaCache';

type MobileAuth = {
  user: MobileUser | null;
  tenantName: string | null;
  tenantLogoUrl: string | null;
  tenantLogoAlt: string;
  membershipsCount: number;
  isLoading: boolean;
  isAuthenticated: boolean;
  hasPermission: (code: string) => boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
};

const MobileAuthContext = createContext<MobileAuth | null>(null);

export function MobileAuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [bypass, setBypass] = useState(false);
  const profileQuery = useQuery({
    queryKey: ['mobile-auth', 'me'],
    queryFn: () => authService.me(),
    retry: false,
    enabled: !bypass,
  });
  const user = profileQuery.data ?? null;
  const membershipsQuery = useQuery<Membership[]>({
    queryKey: ['mobile-auth', 'memberships'],
    queryFn: () => authService.memberships(),
    retry: false,
    enabled: !bypass && user !== null,
  });

  const refresh = useCallback(async () => {
    setBypass(false);
    await queryClient.invalidateQueries({ queryKey: ['mobile-auth'] });
  }, [queryClient]);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } finally {
      setBypass(true);
      await clearReadCaches();
      queryClient.clear();
      window.location.href = '/m/login';
    }
  }, [queryClient]);

  const value = useMemo<MobileAuth>(() => {
    const tenantName =
      membershipsQuery.data?.find((m) => m.tenant_id === user?.active_tenant_id)
        ?.tenant_name ?? null;
    return {
      user,
      tenantName,
      tenantLogoUrl: user?.tenant_logo_url ?? null,
      tenantLogoAlt: user?.tenant_logo_alt_text?.trim() || tenantName || 'Logo de la empresa',
      membershipsCount: membershipsQuery.data?.length ?? 0,
      isLoading: profileQuery.isPending && !bypass,
      isAuthenticated: user !== null,
      hasPermission: (code: string) => user?.permissions.includes(code) ?? false,
      refresh,
      logout,
    };
  }, [membershipsQuery.data, user, profileQuery.isPending, bypass, refresh, logout]);

  return <MobileAuthContext.Provider value={value}>{children}</MobileAuthContext.Provider>;
}

export function useMobileAuth(): MobileAuth {
  const ctx = useContext(MobileAuthContext);
  if (!ctx) throw new Error('useMobileAuth requiere MobileAuthProvider');
  return ctx;
}
